import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import {
  getMentorBySlug, MENTOR_AVAILABILITY, SESSION_TYPES,
  convertPrice, getAvatarUrl
} from '../data/mockData';
import { useEmail, EMAIL_EVENTS } from '../context/EmailContext';
import { useIntegrations } from '../context/IntegrationsContext';
import MeetingPlatformLogo from '../components/MeetingPlatformLogo';
import './BookingPage.css';
import { bookingService, mentorService } from '../services';
import { markReferralConverted } from '../utils/referral';
import API_CONFIG from '../config/api.config';

/* ─── Availability helpers (API mode) ──────────────────────────
   availabilitySlots: [{ day: 0-6, startTime: "09:00", endTime: "17:00" }]
   Returns the next `daysAhead` dates whose weekday is in the mentor's schedule.
*/
function getAvailableDatesFromSlots(availabilitySlots = [], daysAhead = 30) {
  if (!availabilitySlots || availabilitySlots.length === 0) return [];
  const availDays = new Set(availabilitySlots.map(s => s.day));
  const dates = [];
  const today = new Date();
  for (let i = 0; i <= daysAhead; i++) {
    const dt = new Date(today);
    dt.setDate(today.getDate() + i);
    if (availDays.has(dt.getDay())) {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const d = String(dt.getDate()).padStart(2, '0');
      dates.push(`${y}-${m}-${d}`);
    }
  }
  return dates;
}

/* Convert "09:00" → "9:00 AM" for display consistency with mock data */
function fmt24to12(time24 = '') {
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mStr} ${ampm}`;
}

/* ─── Timezone / slot-expiry helpers ────────────────────────
   Parses formats used in both mock data and the API:
     "IST (UTC+5:30)"  →  330
     "MYT (UTC+8)"     →  480
     "WIB (UTC+7)"     →  420
     "Asia/Kolkata"    →  330  (via Intl API)
*/
function getMentorUtcOffsetMins(timezoneStr) {
  if (!timezoneStr) return 0;

  // Fast path: extract "UTC±H:MM" or "UTC±H" embedded in the string
  const utcMatch = timezoneStr.match(/UTC([+-])(\d{1,2})(?::(\d{2}))?/i);
  if (utcMatch) {
    const sign = utcMatch[1] === '+' ? 1 : -1;
    const h    = parseInt(utcMatch[2], 10);
    const m    = parseInt(utcMatch[3] || '0', 10);
    return sign * (h * 60 + m);
  }

  // Slow path: treat the string as an IANA timezone name and use Intl
  try {
    const now       = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezoneStr, timeZoneName: 'longOffset',
    });
    const parts      = formatter.formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value || '';
    const m2 = offsetPart.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (m2) return (m2[1] === '+' ? 1 : -1) * (parseInt(m2[2], 10) * 60 + parseInt(m2[3], 10));
  } catch (_) { /* not a valid IANA name */ }

  return 0; // fallback: treat as UTC
}

/**
 * Returns true when a time slot is within `bufferHours` (default 3) of now.
 * Both the slot time and "now" are expressed in UTC for a fair comparison.
 *
 * @param {string} dateStr       "YYYY-MM-DD" — local date in mentor's timezone
 * @param {string} timeStr       "3:00 PM"    — local time in mentor's timezone
 * @param {string} mentorTz      e.g. "IST (UTC+5:30)" or "Asia/Kolkata"
 * @param {number} bufferHours   minimum lead time required (default 3)
 */
function isSlotTooSoon(dateStr, timeStr, mentorTz, bufferHours = 3) {
  if (!dateStr || !timeStr) return false;

  // Parse "3:00 PM" → 24-hour h/m
  const parts = timeStr.trim().split(' ');
  if (parts.length < 2) return false;
  const [timePart, ampm] = parts;
  let [h, m] = timePart.split(':').map(Number);
  if (ampm?.toUpperCase() === 'PM' && h !== 12) h += 12;
  if (ampm?.toUpperCase() === 'AM' && h === 12) h = 0;

  const [yr, mo, dy] = dateStr.split('-').map(Number);
  const offsetMins   = getMentorUtcOffsetMins(mentorTz);

  // Slot datetime in UTC: subtract mentor's UTC offset from local slot time
  const slotUtcMs = Date.UTC(yr, mo - 1, dy, h, m) - offsetMins * 60_000;

  return (slotUtcMs - Date.now()) < bufferHours * 3_600_000;
}

/* ─── Step indicator ───────────────────────────────────── */
function StepBar({ current }) {
  const steps = ['Choose Session', 'Pick Date & Time', 'Review & Confirm'];
  return (
    <div className="step-bar">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`step-item ${i + 1 === current ? 'active' : ''} ${i + 1 < current ? 'done' : ''}`}>
            <div className="step-circle">
              {i + 1 < current ? '✓' : i + 1}
            </div>
            <span className="step-label">{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`step-line ${i + 1 < current ? 'done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─── Mini calendar ─────────────────────────────────────── */
function MiniCalendar({ availableDates, selectedDate, onSelect }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const monthName = new Date(viewYear, viewMonth).toLocaleString('default', { month: 'long', year: 'numeric' });
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const fmt = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="mini-cal">
      <div className="mc-header">
        <button className="mc-nav" onClick={prevMonth}>‹</button>
        <span className="mc-month">{monthName}</span>
        <button className="mc-nav" onClick={nextMonth}>›</button>
      </div>
      <div className="mc-weekdays">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(w => (
          <div key={w} className="mc-wd">{w}</div>
        ))}
      </div>
      <div className="mc-days">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} />;
          const dateStr = fmt(viewYear, viewMonth, d);
          const isAvail = availableDates.includes(dateStr);
          const isPast = new Date(dateStr) < new Date(today.toDateString());
          const isSelected = selectedDate === dateStr;
          const isToday = dateStr === fmt(today.getFullYear(), today.getMonth(), today.getDate());
          return (
            <button
              key={d}
              disabled={!isAvail || isPast}
              className={`mc-day ${isAvail && !isPast ? 'avail' : ''} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isPast ? 'past' : ''}`}
              onClick={() => isAvail && !isPast && onSelect(dateStr)}
            >
              {d}
            </button>
          );
        })}
      </div>
      <div className="mc-legend">
        <span><span className="legend-dot avail"></span> Available</span>
        <span><span className="legend-dot selected"></span> Selected</span>
      </div>
    </div>
  );
}

/* ─── Booking Summary Card ──────────────────────────────── */
function BookingSummary({ mentor, sessionType, date, time, currency }) {
  if (!mentor) return null;
  // sessionType may be a string name (mock) or an object {name,priceINR} (API)
  const stName   = typeof sessionType === 'object' ? sessionType?.name : sessionType;
  const stObj    = typeof sessionType === 'object' ? sessionType : null;
  const info = SESSION_TYPES[stName] || null;

  // Mirror the same pricing logic as the session card for consistency
  let priceINR, regularINR;
  if (stObj?.originalPriceINR > 0) {
    // API supplies was/offer pair directly
    regularINR = stObj.originalPriceINR;
    priceINR   = stObj.priceINR > 0 ? stObj.priceINR : regularINR;
  } else if (stObj?.priceINR > 0) {
    const dp   = info?.discountPct || 0;
    regularINR = stObj.priceINR;
    priceINR   = dp > 0 ? Math.round(regularINR * (1 - dp / 100)) : regularINR;
    if (priceINR === regularINR) regularINR = null; // no discount — don't show strikethrough
  } else {
    const base = info ? Math.round(mentor.priceINR * info.multiplier) : (mentor.priceINR || 0);
    const dp   = info?.discountPct || 0;
    regularINR = dp > 0 ? base : null;
    priceINR   = dp > 0 ? Math.round(base * (1 - dp / 100)) : base;
  }

  const discountPct  = (regularINR && priceINR && regularINR > priceINR)
    ? Math.round((1 - priceINR / regularINR) * 100) : 0;
  const price        = convertPrice(priceINR, currency);
  const regularPrice = regularINR ? convertPrice(regularINR, currency) : null;
  const displayDate = date
    ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : null;

  return (
    <div className="booking-summary-card">
      <div className="bsc-title">Booking Summary</div>

      <div className="bsc-mentor">
        <img
          src={getAvatarUrl(mentor.avatarSeed, mentor.avatarBg, 48)}
          alt={mentor.name}
          className="bsc-avatar"
          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
        />
        <div className="bsc-avatar-fb" style={{ background: `#${mentor.avatarBg}` }}>{mentor.initials}</div>
        <div>
          <div className="bsc-mentor-name">{mentor.name}</div>
          <div className="bsc-mentor-role">{mentor.role} · {mentor.company}</div>
        </div>
      </div>

      <div className="bsc-rows">
        <div className="bsc-row">
          <span className="bsc-lbl">Session type</span>
          <span className="bsc-val">{stName ? `${info?.icon || ''} ${stName}`.trim() : '—'}</span>
        </div>
        <div className="bsc-row">
          <span className="bsc-lbl">Duration</span>
          <span className="bsc-val">{stObj?.duration ? `${stObj.duration} min` : info?.duration || '—'}</span>
        </div>
        <div className="bsc-row">
          <span className="bsc-lbl">Date</span>
          <span className="bsc-val">{displayDate || '—'}</span>
        </div>
        <div className="bsc-row">
          <span className="bsc-lbl">Time</span>
          <span className="bsc-val">{time || '—'}</span>
        </div>
        <div className="bsc-divider" />
        {regularPrice && (
          <div className="bsc-row">
            <span className="bsc-lbl">Regular price</span>
            <span className="bsc-val bsc-strikethrough">{regularPrice}</span>
          </div>
        )}
        {discountPct > 0 && (
          <div className="bsc-row">
            <span className="bsc-lbl">Discount</span>
            <span className="bsc-val bsc-saving">−{discountPct}% off 🎉</span>
          </div>
        )}
        <div className="bsc-row total">
          <span className="bsc-lbl">You pay</span>
          <span className="bsc-price">{price}</span>
        </div>
      </div>

      <div className="bsc-trust">
        <div className="bsc-trust-item">✅ Instant confirmation</div>
        <div className="bsc-trust-item">🔄 Free reschedule up to 24 hrs before</div>
        <div className="bsc-trust-item">💯 Full refund if mentor no-shows</div>
      </div>
    </div>
  );
}

/* ─── Main Booking Page ─────────────────────────────────── */
export default function BookingPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currency, setModalOpen, showToast } = useContext(AppContext);
  const { user } = useAuth();
  const { getEnabledMeetingPlatforms } = useIntegrations();
  const { sendEmail } = useEmail();
  const enabledPlatforms = getEnabledMeetingPlatforms();

  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const [mentor, setMentor] = React.useState(() => isMock ? getMentorBySlug(slug) : null);

  // Load mentor from API when not in mock mode
  React.useEffect(() => {
    if (isMock) return;
    mentorService.getMentorBySlug(slug)
      .then(res => { if (res?.mentor) setMentor(res.mentor); })
      .catch(() => {});
  }, [slug]); // eslint-disable-line

  // ── Restore saved booking intent (guest → login → return) ──
  const savedIntent = (() => {
    try { return JSON.parse(sessionStorage.getItem('db_booking_intent')); } catch { return null; }
  })();
  const isRestoredIntent = savedIntent && savedIntent.mentorSlug === slug;

  const [loading, setLoading]         = useState(false); // eslint-disable-line
  const [expandedSession, setExpandedSession] = useState(null);  // stN of expanded card
  const [step, setStep]               = useState(isRestoredIntent ? 3 : 1);
  // sessionType can be a string (mock) or object {name, priceINR, duration} (API)
  const rawSessionType = isRestoredIntent ? savedIntent.sessionType : (location.state?.sessionType || '');
  const [sessionType, setSessionType] = useState(typeof rawSessionType === 'object' ? rawSessionType?.name || '' : rawSessionType);
  const [selectedDate, setSelectedDate] = useState(isRestoredIntent ? savedIntent.selectedDate : '');
  const [selectedTime, setSelectedTime] = useState(isRestoredIntent ? savedIntent.selectedTime : '');
  const [note, setNote]               = useState(isRestoredIntent ? savedIntent.note : '');
  const [meetingPlatform, setMeetingPlatform] = useState(isRestoredIntent ? savedIntent.meetingPlatform : 'google_meet');
  const [customMeetingLink, setCustomMeetingLink] = useState(isRestoredIntent ? savedIntent.customMeetingLink : '');

  // ── API mode: time slots for selected date ────────────────
  const [apiTimeSlots, setApiTimeSlots]   = useState([]);
  const [loadingSlots, setLoadingSlots]   = useState(false);

  // Computed availability — mock uses static table, API uses live data
  const mockAvail      = MENTOR_AVAILABILITY[mentor?.id] || { availableDates: [], timeSlots: [] };
  const mentorMaxDays       = mentor?.maxDaysAhead          ?? 30;
  const mentorSameDay       = mentor?.sameDayBooking         ?? true;
  const mentorMinDaysNotice = mentor?.minDaysBeforeBooking   ?? 1;
  const availableDates  = (() => {
    let dates = isMock
      ? mockAvail.availableDates
      : getAvailableDatesFromSlots(mentor?.availabilitySlots, mentorMaxDays);

    // Trim to the mentor's booking window (no dates beyond maxDaysAhead)
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + mentorMaxDays);
    const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth()+1).padStart(2,'0')}-${String(cutoff.getDate()).padStart(2,'0')}`;
    dates = dates.filter(d => d <= cutoffStr);

    // Enforce minimum advance notice:
    // sameDayBooking = true  → no date filtering (hours buffer handles it)
    // sameDayBooking = false → remove any date within minDaysBeforeBooking days of today
    if (!mentorSameDay) {
      const todayMs = (() => {
        const t = new Date(); t.setHours(0, 0, 0, 0); return t.getTime();
      })();
      dates = dates.filter(d => {
        const dateMs = new Date(d + 'T00:00:00').getTime();
        const diffDays = Math.round((dateMs - todayMs) / 86_400_000);
        return diffDays >= mentorMinDaysNotice;
      });
    }
    return dates;
  })();
  const availTimeSlots = isMock ? mockAvail.timeSlots : apiTimeSlots;

  // ── Auto-select first available date (and cascade-load its slots) ──
  useEffect(() => {
    if (isRestoredIntent || selectedDate) return;
    if (availableDates.length > 0) {
      setSelectedDate(availableDates[0]);
    }
  }, [mentor, isMock]); // eslint-disable-line

  // ── Fetch time slots from API whenever date changes (API mode only) ──
  useEffect(() => {
    if (isMock || !selectedDate || !mentor) return;
    setLoadingSlots(true);
    setApiTimeSlots([]);
    mentorService.getAvailability(mentor._id || mentor.id, selectedDate)
      .then(res => {
        const available = (res?.slots || [])
          .filter(s => s.available)
          .map(s => fmt24to12(s.time));
        setApiTimeSlots(available);
      })
      .catch(() => setApiTimeSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]); // eslint-disable-line

  // ── Auto-complete booking if user just logged in ──────────
  useEffect(() => {
    if (user && isRestoredIntent) {
      // Small delay so page renders first
      const timer = setTimeout(() => completeBooking(user), 600);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!mentor) {
    return (
      <div className="book-not-found">
        <p>Mentor not found. <Link to="/mentors">Browse mentors →</Link></p>
      </div>
    );
  }

  // Self-booking guard — compare logged-in user's ID with the mentor's userId
  const mentorUserId = mentor.userId
    ? (typeof mentor.userId === 'object' ? String(mentor.userId._id) : String(mentor.userId))
    : null;
  const loggedInUserId = user ? String(user._id || user.id) : null;
  const isSelfBooking  = !isMock && mentorUserId && loggedInUserId && mentorUserId === loggedInUserId;

  if (isSelfBooking) {
    return (
      <div className="book-not-found">
        <p style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 8 }}>
          🚫 You can't book a session with yourself.
        </p>
        <p style={{ color: '#6b7280', marginBottom: 20 }}>
          This is your own mentor profile. Share it with mentees so they can book you!
        </p>
        <Link to="/dashboard/mentor" className="btn btn-primary">Go to your Dashboard →</Link>
      </div>
    );
  }

  // sessionType may be a display string (mock) or object {name,priceINR,duration} (API)
  const stName   = typeof sessionType === 'object' ? sessionType?.name : sessionType;
  const stObj    = typeof sessionType === 'object' ? sessionType : null;
  // Find matching sessionType object from mentor.sessionTypes if sessionType is a string
  const stFromMentor = typeof sessionType === 'string'
    ? (mentor.sessionTypes || []).find(s =>
        (s.name || s.id || s) === sessionType ||
        (typeof s === 'string' && s === sessionType))
    : null;
  const info = SESSION_TYPES[stName] || null;

  // Resolve pricing using the same priority as the session card:
  // API object with originalPriceINR → API object priceINR → stFromMentor → multiplier fallback
  const stResolved = stObj || stFromMentor;
  let priceINR;
  if (stResolved?.originalPriceINR > 0) {
    // API has a "was" price — offer price is the actual charge
    priceINR = stResolved.priceINR > 0 ? stResolved.priceINR : stResolved.originalPriceINR;
  } else if (stResolved?.priceINR > 0) {
    // API has only priceINR — apply local discountPct if any
    const dp = info?.discountPct || 0;
    priceINR = dp > 0 ? Math.round(stResolved.priceINR * (1 - dp / 100)) : stResolved.priceINR;
  } else {
    // Mock string — derive from multiplier + discountPct
    const base = info ? Math.round(mentor.priceINR * info.multiplier) : (mentor.priceINR || 0);
    const dp   = info?.discountPct || 0;
    priceINR   = dp > 0 ? Math.round(base * (1 - dp / 100)) : base;
  }

  /* ─ Step handlers ─ */
  const canGoStep2 = !!sessionType;
  const canGoStep3 = !!selectedDate && !!selectedTime;

  const handleConfirmBooking = () => {
    if (user) {
      // Already logged in — process booking directly
      completeBooking(user);
    } else {
      // Save full booking intent so AuthModal can resume after login
      const intent = {
        mentorSlug:     slug,
        returnUrl:      window.location.pathname,
        sessionType,
        selectedDate,
        selectedTime,
        meetingPlatform,
        customMeetingLink,
        note,
        priceINR,
        mentorName:     mentor.name,
        savedAt:        Date.now(),
      };
      sessionStorage.setItem('db_booking_intent', JSON.stringify(intent));
      setModalOpen(true);
    }
  };

  // Called after login (or directly if already logged in)
  const completeBooking = async (bookingUser) => {
    sessionStorage.removeItem('db_booking_intent');
    const platform = enabledPlatforms.find(p => p.id === meetingPlatform);

    if (API_CONFIG.MOCK) {
      // ── MOCK flow ──────────────────────────────────────────
      showToast('🎉', 'Booking Confirmed!', `Session with ${mentor.name} on ${selectedDate} at ${selectedTime}`);
      sendEmail(EMAIL_EVENTS.BOOKING_CONFIRMED_MENTEE, {
        menteeName: bookingUser?.firstName || 'You',
        menteeEmail: bookingUser?.email || '',
        mentorName: mentor.name, sessionDate: selectedDate, sessionTime: selectedTime,
        duration: info?.duration || '60', sessionType: stName || '1:1 Mentoring',
        platform: platform?.label || 'Google Meet', meetLink: 'Link will be sent by mentor',
        amountPaid: `₹${priceINR?.toLocaleString()}`,
        dashboardUrl: `${window.location.origin}/dashboard/mentee`,
      });
      navigate('/dashboard/mentee');
      return;
    }

    // ── REAL flow: create booking → confirm directly (payment disabled) ──
    // TODO: Re-enable Razorpay by replacing this block with payment flow
    try {
      setLoading(true);

      const bookingRes = await bookingService.createBooking({
        mentorId:    mentor._id || mentor.id,
        sessionType: sessionType,
        sessionDate: selectedDate,
        sessionTime: selectedTime,
        date:        selectedDate,
        time:        selectedTime,
        platform:    meetingPlatform,
        meetingPlatform: meetingPlatform,
        priceINR:    priceINR,
        notes: note,
      });

      const bookingId = bookingRes.booking?._id || bookingRes.booking?.id;

      // Mark as confirmed directly (payment skipped)
      if (bookingId) {
        await bookingService.confirmBooking(bookingId).catch(() => {});
      }

      markReferralConverted();
      showToast('🎉', 'Session Booked!', `Session with ${mentor.name} on ${selectedDate} confirmed!`);
      navigate('/dashboard/mentee');

    } catch (err) {
      console.error('Booking error:', err);
      showToast('❌', 'Booking Failed', err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking-page">

      {/* ── Top bar ── */}
      <div className="booking-topbar">
        <div className="booking-topbar-inner">
          <Link to={`/mentors/${mentor.slug}`} className="back-link">
            ← Back to {mentor.name}'s Profile
          </Link>
          <div className="booking-topbar-mentor">
            <img
              src={getAvatarUrl(mentor.avatarSeed, mentor.avatarBg, 36)}
              alt={mentor.name}
              className="topbar-avatar"
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <div className="topbar-av-fb" style={{ background: `#${mentor.avatarBg}` }}>{mentor.initials}</div>
            <span className="topbar-mentor-name">{mentor.name}</span>
          </div>
        </div>
      </div>

      <div className="booking-inner">

        {/* ── Step bar ── */}
        <StepBar current={step} />

        <div className="booking-layout">
          {/* ── Left: Step content ── */}
          <div className="booking-left">

            {/* ── STEP 1: Choose Session Type ── */}
            {step === 1 && (
              <div className="booking-step">
                <h2 className="booking-step-title">Choose Session Type</h2>
                <p className="booking-step-sub">
                  {user
                    ? 'Choose your session type to get started.'
                    : 'All sessions are publicly viewable. You only need to sign in when you confirm your booking.'}
                </p>

                <div className="session-options">
                  {(mentor.sessionTypes || []).map((st, idx) => {
                    // st is a plain string (mock) OR a full object from the API / normalizeMentor.
                    // Data priority: API object fields → SESSION_TYPES local metadata → defaults.
                    const isObj = typeof st === 'object' && st !== null;
                    const stN   = isObj ? (st.name || st.id || '1:1 Session') : String(st || '');

                    // Local SESSION_TYPES entry acts as an enrichment layer for both modes.
                    const siMeta = SESSION_TYPES[stN] || {};

                    // ── Pricing ────────────────────────────────────────────────
                    // API mode: st.originalPriceINR = "was" price, st.priceINR = offer price.
                    // Mock mode: derive from multiplier + optional discountPct.
                    let basePrice, offerPrice;
                    if (isObj && st.originalPriceINR > 0) {
                      // API supplies both — use directly
                      basePrice  = st.originalPriceINR;
                      offerPrice = st.priceINR > 0 ? st.priceINR : basePrice;
                    } else if (isObj) {
                      // API has only priceINR (no original) — apply local discountPct if any
                      basePrice  = st.priceINR || mentor.priceINR || 0;
                      const dp   = siMeta.discountPct || 0;
                      offerPrice = dp > 0 ? Math.round(basePrice * (1 - dp / 100)) : basePrice;
                    } else {
                      // Mock string — compute from multiplier + discountPct
                      basePrice  = siMeta.multiplier != null
                        ? Math.round((mentor.priceINR || 0) * siMeta.multiplier)
                        : (mentor.priceINR || 0);
                      const dp   = siMeta.discountPct || 0;
                      offerPrice = dp > 0 ? Math.round(basePrice * (1 - dp / 100)) : basePrice;
                    }
                    const hasDiscount   = offerPrice < basePrice;
                    const discountShown = hasDiscount
                      ? Math.round((1 - offerPrice / basePrice) * 100)
                      : 0;

                    // ── Display metadata ───────────────────────────────────────
                    // API fields take priority; SESSION_TYPES fills in the rest.
                    const durLabel = isObj
                      ? `${st.duration || st.durationMins || 60} min`
                      : (siMeta.duration || '60 min');
                    const icon  = (isObj ? st.icon        : null) || siMeta.icon  || '🎯';
                    const desc  = (isObj ? st.description : null) || siMeta.desc  || '';

                    // Badge: prefer SESSION_TYPES badge; fall back to auto-computed discount badge
                    const badge      = siMeta.badge
                      || (discountShown > 0 ? `${discountShown}% off` : null);
                    const badgeColor = siMeta.badgeColor
                      || (discountShown > 0 ? 'amber' : 'green');

                    // "What's included" details — SESSION_TYPES has curated copy;
                    // for API sessions with no match, fall back to st.topics as bullets.
                    const includes      = siMeta.includes?.length
                      ? siMeta.includes
                      : (isObj && st.topics?.length ? st.topics : []);
                    const idealFor      = siMeta.idealFor      || '';
                    const whatToPrepare = siMeta.whatToPrepare || '';
                    const isSelected = typeof sessionType === 'object'
                      ? sessionType?.name === stN
                      : sessionType === stN;
                    const isExpanded = expandedSession === stN;

                    return (
                      <div
                        key={stN + idx}
                        className={`session-option ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
                      >
                        {/* ── Clickable main row ── */}
                        <div
                          className="so-main-row"
                          onClick={() => setSessionType(isObj ? st : stN)}
                        >
                          <div className="so-left">
                            <div className="so-ico">{icon}</div>
                            <div className="so-radio">
                              <div className={`so-dot ${isSelected ? 'on' : ''}`} />
                            </div>
                          </div>

                          <div className="so-body">
                            <div className="so-name-row">
                              <span className="so-name">{stN}</span>
                              {badge && (
                                <span className={`so-badge so-badge--${badgeColor}`}>{badge}</span>
                              )}
                            </div>
                            <div className="so-desc">{desc}</div>
                            <div className="so-dur">⏱ {durLabel}</div>
                          </div>

                          <div className="so-price-col">
                            {hasDiscount ? (
                              <>
                                <div className="so-price-regular">{convertPrice(basePrice, currency)}</div>
                                <div className="so-price-offer">{convertPrice(offerPrice, currency)}</div>
                                <div className="so-discount-badge">{discountShown}% off</div>
                              </>
                            ) : (
                              <div className="so-price">{convertPrice(offerPrice, currency)}</div>
                            )}
                          </div>
                        </div>

                        {/* ── Know more toggle ── */}
                        {(includes.length > 0 || idealFor) && (
                          <button
                            className={`so-expand-btn ${isExpanded ? 'open' : ''}`}
                            onClick={e => {
                              e.stopPropagation();
                              setExpandedSession(isExpanded ? null : stN);
                            }}
                          >
                            {isExpanded ? 'Hide details ▲' : 'What\'s included ▾'}
                          </button>
                        )}

                        {/* ── Expanded details ── */}
                        {isExpanded && (
                          <div className="so-details">
                            {includes.length > 0 && (
                              <div className="so-details-section">
                                <div className="so-details-label">✅ What's included</div>
                                <ul className="so-includes">
                                  {includes.map((item, i) => (
                                    <li key={i}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {idealFor && (
                              <div className="so-details-section">
                                <div className="so-details-label">👤 Ideal for</div>
                                <p className="so-details-text">{idealFor}</p>
                              </div>
                            )}
                            {whatToPrepare && (
                              <div className="so-details-section">
                                <div className="so-details-label">📋 How to prepare</div>
                                <p className="so-details-text">{whatToPrepare}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="step-actions">
                  <div />
                  <button
                    className="btn btn-primary btn-lg"
                    disabled={!canGoStep2}
                    onClick={() => setStep(2)}
                  >
                    Continue → Pick a Date
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 2: Date & Time ── */}
            {step === 2 && (
              <div className="booking-step">
                <h2 className="booking-step-title">Pick Date & Time</h2>
                <p className="booking-step-sub">
                  Highlighted dates are available. All times shown in {mentor.timezone}.
                </p>

                <div className="datetime-layout">
                  <div>
                    <div className="dt-label">Select a date</div>
                    <MiniCalendar
                      availableDates={availableDates}
                      selectedDate={selectedDate}
                      onSelect={(d) => { setSelectedDate(d); setSelectedTime(''); }}
                    />
                  </div>

                  <div className="timeslots-col">
                    <div className="dt-label">
                      {selectedDate
                        ? `Available times for ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}`
                        : 'Select a date to see available times'}
                    </div>
                    {selectedDate ? (
                      loadingSlots ? (
                        <div className="timeslots-empty">
                          <span style={{ fontSize: '1.5rem', marginBottom: 8, display: 'block' }}>⏳</span>
                          Loading available slots…
                        </div>
                      ) : availTimeSlots.length === 0 ? (
                        <div className="timeslots-empty">
                          <span style={{ fontSize: '1.5rem', marginBottom: 8, display: 'block' }}>😔</span>
                          No slots available for this date
                        </div>
                      ) : (() => {
                        // Annotate each slot with its availability status
                        const mentorMinHours = mentor?.minHoursBeforeBooking ?? 3;
                        const annotated = availTimeSlots.map(t => ({
                          time: t,
                          tooSoon: isSlotTooSoon(selectedDate, t, mentor.timezone, mentorMinHours),
                        }));
                        const bookableCount = annotated.filter(s => !s.tooSoon).length;

                        // If the currently selected time just became expired, clear it
                        if (selectedTime && annotated.find(s => s.time === selectedTime)?.tooSoon) {
                          setSelectedTime('');
                        }

                        return (
                          <>
                            <div className="timeslots-grid">
                              {annotated.map(({ time: t, tooSoon }) => (
                                <button
                                  key={t}
                                  disabled={tooSoon}
                                  title={tooSoon ? 'Must book at least 3 hours in advance' : ''}
                                  className={`timeslot
                                    ${selectedTime === t ? 'selected' : ''}
                                    ${tooSoon ? 'slot-past' : ''}
                                  `}
                                  onClick={() => !tooSoon && setSelectedTime(t)}
                                >
                                  {t}
                                  {tooSoon && <span className="slot-past-label">passed</span>}
                                </button>
                              ))}
                            </div>
                            {bookableCount === 0 && (
                              <div className="timeslots-empty" style={{ marginTop: 12 }}>
                                <span style={{ fontSize: '1.5rem', marginBottom: 8, display: 'block' }}>⏱</span>
                                All slots for today have passed. Pick another date.
                              </div>
                            )}
                            {bookableCount > 0 && bookableCount < annotated.length && (
                              <p className="slots-buffer-note">
                                ⏱ Greyed slots are within 3 hours — pick a later time or another date.
                              </p>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      <div className="timeslots-empty">
                        <span style={{ fontSize: '2rem', marginBottom: 8, display: 'block' }}>📅</span>
                        Pick a date on the calendar first
                      </div>
                    )}
                  </div>
                </div>

                <div className="step-actions">
                  <button className="btn btn-outline btn-lg" onClick={() => setStep(1)}>← Back</button>
                  <button
                    className="btn btn-primary btn-lg"
                    disabled={!canGoStep3}
                    onClick={() => setStep(3)}
                  >
                    Continue → Review
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 3: Review & Confirm ── */}
            {step === 3 && (
              <div className="booking-step">
                <h2 className="booking-step-title">Review & Confirm</h2>
                <p className="booking-step-sub">
                  {user
                    ? `Everything looks good, ${user.name?.split(' ')[0] || 'there'}! Review your details and confirm.`
                    : "Everything looks good? Click Confirm to proceed — you'll be asked to sign in or create a free account."}
                </p>

                {/* Summary detail */}
                <div className="confirm-details">
                  <div className="cd-row">
                    <span className="cd-icon">🎯</span>
                    <div>
                      <div className="cd-key">Session Type</div>
                      <div className="cd-val">{info?.icon || '🎯'} {stName} · {stObj?.duration ? `${stObj.duration} min` : info?.duration || '60 min'}</div>
                    </div>
                  </div>
                  <div className="cd-row">
                    <span className="cd-icon">📅</span>
                    <div>
                      <div className="cd-key">Date</div>
                      <div className="cd-val">
                        {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                    </div>
                  </div>
                  <div className="cd-row">
                    <span className="cd-icon">🕐</span>
                    <div>
                      <div className="cd-key">Time</div>
                      <div className="cd-val">{selectedTime} · {mentor.timezone}</div>
                    </div>
                  </div>
                  <div className="cd-row">
                    <span className="cd-icon">👤</span>
                    <div>
                      <div className="cd-key">Mentor</div>
                      <div className="cd-val">{mentor.name} · {mentor.role}</div>
                    </div>
                  </div>
                  <div className="cd-row">
                    <span className="cd-icon">💰</span>
                    <div>
                      <div className="cd-key">Total Amount</div>
                      <div className="cd-val cd-price">{convertPrice(priceINR, currency)}</div>
                    </div>
                  </div>
                </div>

                {/* Optional note */}
                <div className="booking-note-wrap">
                  <label className="note-label">📝 Add a note for your mentor (optional)</label>
                  <textarea
                    className="booking-note"
                    rows={3}
                    placeholder="E.g. I'm struggling with React Context API and want to understand when to use Redux instead. I have 1 year of experience..."
                    value={note}
                    onChange={e => setNote(e.target.value)}
                  />
                </div>

                {/* ── Meeting Platform Selector ── */}
                <div className="meeting-platform-section">
                  <div className="mp-title">Choose Meeting Platform</div>
                  <div className="mp-subtitle">Your session will be conducted via your selected platform. Meeting link will be shared after booking confirmation.</div>
                  <div className="mp-grid">
                    {enabledPlatforms.map(p => (
                      <button
                        key={p.id}
                        className={`mp-option ${meetingPlatform === p.id ? 'selected' : ''}`}
                        onClick={() => setMeetingPlatform(p.id)}
                        style={{ '--mp-color': p.color }}
                      >
                        <div className="mp-option-left">
                          <MeetingPlatformLogo id={p.id} size={36} className="mp-logo" />
                          <div>
                            <div className="mp-name">{p.label}</div>
                            <div className="mp-hint">{p.hint}</div>
                          </div>
                        </div>
                        <div className={`mp-radio-dot ${meetingPlatform === p.id ? 'on' : ''}`} />
                      </button>
                    ))}
                  </div>
                  {meetingPlatform === 'custom' && (
                    <div style={{ marginTop: 12 }}>
                      <label className="note-label">🔗 Your Custom Meeting Link</label>
                      <input
                        className="booking-note"
                        style={{ padding: '10px 14px', borderRadius: 10 }}
                        placeholder={enabledPlatforms.find(p => p.id === 'custom').placeholder}
                        value={customMeetingLink}
                        onChange={e => setCustomMeetingLink(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="mp-selected-display">
                    <span style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <MeetingPlatformLogo id={meetingPlatform} size={20} />
                      <span>Selected: <strong>{enabledPlatforms.find(p => p.id === meetingPlatform)?.label}</strong></span>
                    </span>
                    {meetingPlatform !== 'custom' && <span className="mp-link-note"> · Link will be sent by mentor after confirmation</span>}
                  </div>
                </div>

                {/* Payment info */}
                <div className="payment-info">
                  <div className="pi-head">
                    <span className="pi-ico">✅</span>
                    <div>
                      <div className="pi-title">Confirm Your Booking</div>
                      <div className="pi-sub">Your session will be instantly confirmed — payment integration coming soon</div>
                    </div>
                  </div>
                </div>

                {/* 🔐 LOGIN GATE — only shown to guests */}
                {!user && (
                  <div className="login-gate-banner">
                    <div className="lgb-icon">🔐</div>
                    <div className="lgb-body">
                      <div className="lgb-title">Almost there! Sign in to confirm</div>
                      <div className="lgb-sub">
                        ✅ Your slot is saved. Sign in or create a free account — we'll bring you right back here.
                        <strong> No payment until you confirm.</strong>
                      </div>
                      <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                        <span style={{ background:'#f0fdf4', color:'#16a34a', padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>📅 {isRestoredIntent ? savedIntent.selectedDate : selectedDate}</span>
                        <span style={{ background:'#f0fdf4', color:'#16a34a', padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>⏰ {isRestoredIntent ? savedIntent.selectedTime : selectedTime}</span>
                        <span style={{ background:'#f0fdf4', color:'#16a34a', padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>💼 {isRestoredIntent ? savedIntent.sessionType : sessionType}</span>
                      </div>
                    </div>
                  </div>
                )}
                {user && (
                  <div className="login-gate-banner logged-in-banner">
                    <div className="lgb-icon">✅</div>
                    <div className="lgb-body">
                      <div className="lgb-title">Booking as {user.name}</div>
                      <div className="lgb-sub">
                        Logged in as <strong>{user.email}</strong>. Click Confirm Booking to proceed to payment.
                      </div>
                    </div>
                  </div>
                )}

                <div className="step-actions">
                  <button className="btn btn-outline btn-lg" onClick={() => setStep(2)}>← Back</button>
                  <button
                    className="btn btn-cta btn-lg"
                    style={{ minWidth: 240 }}
                    onClick={handleConfirmBooking}
                  >
                    {user ? '✅ Confirm Booking' : '🔐 Sign In & Confirm Booking'}
                  </button>
                </div>

                <p className="booking-footer-note">
                  By confirming, you agree to our <a href="#terms">Terms of Service</a> and <a href="#refund">Refund Policy</a>.
                  Your card will only be charged after you complete payment on the next screen.
                </p>
              </div>
            )}
          </div>

          {/* ── Right: sticky summary ── */}
          <div className="booking-right">
            <BookingSummary
              mentor={mentor}
              sessionType={sessionType}
              date={selectedDate}
              time={selectedTime}
              currency={currency}
            />

            <div className="more-mentors-card">
              <div style={{ fontSize: '.8rem', color: 'var(--ink3)', fontWeight: 600, marginBottom: 10 }}>
                Not sure? Browse more mentors
              </div>
              <Link to="/mentors" className="btn btn-outline" style={{ width: '100%', justifyContent: 'center', display: 'flex', fontSize: '.84rem' }}>
                ← Back to All Mentors
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
